import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, FileSpreadsheet, FileText, Filter, Loader2, RefreshCw, X } from 'lucide-react';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import BaseSelectField from '../common/SelectField.js';
import {
  filterInputClass,
  filterDateInputClass,
  filterSelectClass,
  selectDropdownOpenClass,
} from '../common/uiClasses.js';
import GstCollectionReportView from './GstCollectionReportView.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { useAuth } from '../../context/AuthContext.js';
import reportService from '../../services/ReportService.js';
import userService from '../../services/UserService.js';
import vehicleService from '../../services/vehicleService.js';
import { locationHubService } from '../../services/locationHubService.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import externalCommissionService from '../../services/ExternalCommissionService.js';
import expenseService from '../../services/ExpenseService.js';
import contractPartyService from '../../services/contractPartyService.js';
import { formatHubBranchLabel, formatUserWithLocation, formatUserNameRoleHub, filterShipmentLocations, destinationTypeLabel } from '../../utils/destinationType.js';
import { formatCurrencyWhole, formatReportCell } from '../../utils/formatCurrency.js';
import LocationTargetBoard, { TargetProgressBar } from '../dashboard/components/LocationTargetBoard.js';
import {
  REPORT_DEFS,
  buildCleanParams,
  canExportExcel,
  colorMap,
  getDefaultFilters,
  isReportVisible,
} from './reportConfig.js';

/** Same label / trigger styles as Shipment list filters */
const filterLabelClass = 'block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2';
const filterTriggerClass =
  'w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl cursor-pointer flex justify-between items-center bg-white text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-400';

/** Equal-width cell wrapper used by every filter field */
const FilterField = ({ children, className = '' }) => (
  <div className={`min-w-0 w-full max-w-full ${className}`}>{children}</div>
);

const ReportSelect = ({ value, onChange, placeholder, options = [], label }) => (
  <FilterField>
    <BaseSelectField
      variant="filter"
      label={label}
      labelClassName={filterLabelClass}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      emptyLabel={placeholder}
      options={[
        { value: '', label: placeholder || 'All' },
        ...(options || []).map((opt) => ({ value: opt.value, label: opt.label })),
      ]}
    />
  </FilterField>
);

const TextInput = ({ value, onChange, placeholder, label }) => (
  <FilterField>
    {label && <label className={filterLabelClass}>{label}</label>}
    <SearchSubmitInput
      value={value}
      onSearch={onChange}
      placeholder={placeholder}
      inputClassName={filterInputClass}
    />
  </FilterField>
);

const DateRangeRow = ({ fromValue, toValue, onFromChange, onToChange, label }) => (
  <FilterField className="sm:col-span-2">
    {label && <label className={filterLabelClass}>{label}</label>}
    <div className="grid grid-cols-1 min-[420px]:grid-cols-[1fr_auto_1fr] items-center gap-1.5 w-full">
      <input
        type="date"
        value={fromValue}
        onChange={(e) => onFromChange(e.target.value)}
        className={filterDateInputClass}
        aria-label="From date"
      />
      <span className="hidden min-[420px]:inline text-xs text-gray-400 text-center shrink-0 px-0.5">to</span>
      <input
        type="date"
        value={toValue}
        onChange={(e) => onToChange(e.target.value)}
        className={filterDateInputClass}
        aria-label="To date"
      />
    </div>
  </FilterField>
);

const CheckboxDropdown = ({ label, options, selected = [], onChange, emptyLabel, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const count = selected.length;

  useEffect(() => {
    if (!open || disabled) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, disabled]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const selectedLabels = (options || [])
    .filter((o) => selected.includes(o.value))
    .map((o) => o.label);

  return (
    <FilterField>
      <div ref={ref} className={`relative w-full ${open ? 'z-[80]' : ''}`}>
        <label className={filterLabelClass}>{label}</label>
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={() => { if (!disabled) setOpen((v) => !v); }}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          className={`${filterTriggerClass} ${open ? selectDropdownOpenClass : ''} ${disabled ? 'opacity-70 cursor-not-allowed bg-slate-50' : ''}`}
          aria-disabled={disabled}
        >
          <span className={`min-w-0 flex-1 truncate pr-2 ${count ? 'text-gray-800' : 'text-gray-400'}`}>
            {count ? selectedLabels.join(', ') : (emptyLabel || `All ${label}`)}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180 text-blue-500' : ''}`}
            aria-hidden="true"
          />
        </div>
        {open && (
          <div
            className="absolute z-50 top-full left-0 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-h-48 overflow-y-auto">
              {count > 0 && (
                <div
                  onClick={() => onChange([])}
                  className="px-3 py-2 text-xs text-blue-600 font-semibold hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                >
                  Clear selection
                </div>
              )}
              {(options || []).map(({ value, label: optLabel }) => {
                const checked = selected.includes(value);
                return (
                  <label
                    key={value}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm sm:text-base text-gray-800"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        onChange(
                          checked
                            ? selected.filter((x) => x !== value)
                            : [...selected, value],
                        );
                      }}
                      className="accent-blue-500"
                    />
                    {optLabel}
                  </label>
                );
              })}
              {(options || []).length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400">No options</div>
              )}
            </div>
          </div>
        )}
      </div>
    </FilterField>
  );
};

const REPORT_TYPE_ALIASES = {
  dispatch: 'ml-sheet',
  ogcl: 'ml-sheet',
  ogpl: 'ml-sheet',
  'profit-loss': 'route-pl',
  pl: 'route-pl',
  route_pl: 'route-pl',
  delivery: 'delivery-event',
};

const REPORT_PAGE_SIZE = 10;

function mergeNamedRows(prev = [], incoming = [], withMeta = false) {
  if (!prev.length) return incoming || [];
  return prev.map((item, index) => {
    const add = (incoming || [])[index] || {};
    const next = {
      ...item,
      ...add,
      rows: [...(item.rows || []), ...(add.rows || [])],
    };
    if (withMeta) {
      next.row_meta = [...(item.row_meta || []), ...(add.row_meta || [])];
    }
    return next;
  });
}

function mergeReportPage(prev, incoming, page) {
  if (!prev || page <= 1) return incoming;
  return {
    ...prev,
    ...incoming,
    rows: [...(prev.rows || []), ...(incoming.rows || [])],
    totals: incoming.totals ?? prev.totals,
    columns: incoming.columns?.length ? incoming.columns : prev.columns,
    sections: mergeNamedRows(prev.sections, incoming.sections, true),
    meta: {
      ...(prev.meta || {}),
      ...(incoming.meta || {}),
      extra_tables: mergeNamedRows(prev.meta?.extra_tables, incoming.meta?.extra_tables),
    },
    pagination: incoming.pagination || prev.pagination,
  };
}

const ReportDetailPage = () => {
  const { type: typeParam } = useParams();
  const type = REPORT_TYPE_ALIASES[typeParam] || typeParam;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const def = REPORT_DEFS[type];
  const roleName = user?.role_name || user?.role;
  const isAgentUser = roleName === 'Agent';
  const isHubAdminUser = roleName === 'Hub Admin';
  const lockedHubIds = useMemo(() => {
    if (type !== 'daily-branch' && type !== 'location') return null;
    if (!(isAgentUser || isHubAdminUser)) return null;
    const hubId = user?.location_hub_id;
    if (hubId == null || hubId === '') return null;
    return [String(hubId)];
  }, [type, isAgentUser, isHubAdminUser, user?.location_hub_id]);

  const applyScopedFilters = useCallback((base) => {
    if (!lockedHubIds) return base;
    return { ...base, location_hub_ids: lockedHubIds };
  }, [lockedHubIds]);

  const [filters, setFilters] = useState(() => getDefaultFilters(def));
  const [payload, setPayload] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const filtersRef = useRef(filters);
  const sentinelRef = useRef(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const iso = (value) => value && /^\d{4}-\d{2}-\d{2}$/.test(value);
    const qDate = searchParams.get('report_date');
    const qFrom = searchParams.get('from_date');
    const qTo = searchParams.get('to_date');
    if (type === 'daily-branch' && (iso(qFrom) || iso(qTo) || iso(qDate))) {
      setFilters((prev) => ({
        ...prev,
        from_date: iso(qFrom) ? qFrom : (iso(qDate) ? qDate : prev.from_date),
        to_date: iso(qTo) ? qTo : (iso(qDate) ? qDate : prev.to_date),
      }));
      return;
    }
    if (iso(qDate)) {
      setFilters((prev) => ({ ...prev, report_date: qDate }));
    }
  }, [searchParams, type]);

  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicleOwners, setVehicleOwners] = useState([]);
  const [externalAgents, setExternalAgents] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [contractParties, setContractParties] = useState([]);

  useEffect(() => {
    if (!user || !def || !isReportVisible(def, user)) return undefined;
    const toArray = (d) => {
      if (Array.isArray(d)) return d;
      if (!d) return [];
      for (const key of ['users', 'vehicles', 'hubs', 'location_hubs', 'clients', 'data', 'results', 'items']) {
        if (Array.isArray(d[key])) return d[key];
        if (d.data && Array.isArray(d.data[key])) return d.data[key];
      }
      if (Array.isArray(d.data)) return d.data;
      return [];
    };
    userService.getAll(null, null, { all: true }).then((d) => setUsers(toArray(d))).catch(() => setUsers([]));
    vehicleService.getAllVehicles(null, null, { all: true }).then((d) => setVehicles(toArray(d))).catch(() => setVehicles([]));
    locationHubService.getAllHubs(null, null, { all: true })
      .then((d) => setLocations(toArray(d?.data ?? d)))
      .catch(() => setLocations([]));
    shipmentClientService.getAll(null, null, { all: true }).then((d) => setClients(toArray(d))).catch(() => setClients([]));
    if (type === 'expense' || type === 'approved-transactions') {
      expenseService.getCategories()
        .then((res) => {
          const list = toArray(res?.data ?? res);
          setExpenseCategories(
            list.map((c) => ({
              value: c.value || c.id || c,
              label: c.label || String(c.value || c).replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
            })),
          );
        })
        .catch(() => setExpenseCategories([]));
    }
    if (type === 'external-commission') {
      externalCommissionService.getVehicleOwners()
        .then((res) => setVehicleOwners(toArray(res?.data?.data ?? res?.data ?? res)))
        .catch(() => setVehicleOwners([]));
      externalCommissionService.loadBulkSettlementAgents()
        .then((agents) => setExternalAgents(agents || []))
        .catch(() => setExternalAgents([]));
    }
    if (type === 'commission') {
      contractPartyService.getAllUnpaginated()
        .then((res) => {
          const list = toArray(res?.data ?? res);
          setContractParties(list);
        })
        .catch(() => setContractParties([]));
    }
    return undefined;
  }, [user, def, type]);

  const hubsById = useMemo(() => {
    const map = {};
    (locations || []).forEach((l) => {
      if (l?.id != null) {
        map[l.id] = l;
        map[String(l.id)] = l;
      }
    });
    return map;
  }, [locations]);

  const locationLabel = (l) => formatHubBranchLabel(l, hubsById) || l?.location || l?.hub_name || l?.name || '';
  const hubOptionLabel = (l) => {
    const name = locationLabel(l);
    const kind = destinationTypeLabel(l?.destination_type);
    return kind ? `${name} (${kind})` : name;
  };
  const locationOptions = filterShipmentLocations(locations || []).map((l) => ({
    value: locationLabel(l) || l.location,
    label: locationLabel(l),
  }));
  const hubIdOptions = filterShipmentLocations(locations || []).map((l) => ({
    value: l.id != null ? String(l.id) : '',
    label: hubOptionLabel(l),
  })).filter((o) => o.value);
  const userOptions = (users || [])
    .filter((u) => u.user_id)
    .map((u) => ({
      value: String(u.user_id),
      label: formatUserWithLocation(u, hubsById),
    }));
  const dailyBranchUserOptions = (users || [])
    .filter((u) => u.user_id)
    .filter((u) => {
      const hid = u.location_hub_id != null ? String(u.location_hub_id) : '';
      if (lockedHubIds) return hid && lockedHubIds.includes(hid);
      const selected = (filters.location_hub_ids || []).map(String).filter(Boolean);
      if (!selected.length) return true;
      return hid && selected.includes(hid);
    })
    .map((u) => ({
      value: String(u.user_id),
      label: formatUserNameRoleHub(u, hubsById),
    }));
  const agentOptions = userOptions.filter((u) => {
    const raw = (users || []).find((x) => String(x.user_id) === String(u.value));
    const role = typeof raw?.role === 'string' ? raw.role : (raw?.role_name || raw?.role?.role_name || '');
    return role === 'Agent';
  });
  const contractPartyOptions = (contractParties || []).map((p) => ({
    value: p.id != null ? String(p.id) : '',
    label: p.name || p.company_name || `Party ${p.id}`,
  }));
  const vehicleOptions = (vehicles || []).map((v) => ({
    value: v.id || v.vehicle_id,
    label: `${v.vehicle_name || v.name || ''} – ${v.vehicle_number || v.number || ''}`,
  }));
  const clientOptions = (clients || []).map((c) => ({
    value: String(c.id || c.client_id || ''),
    label: c.company_name || c.name || '',
  })).filter((o) => o.value);

  const setF = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const setDailyBranchHubs = (hubIds) => {
    setFilters((prev) => {
      const next = { ...prev, location_hub_ids: hubIds };
      if (prev.user_id) {
        const match = (users || []).find((u) => String(u.user_id) === String(prev.user_id));
        const hid = match?.location_hub_id != null ? String(match.location_hub_id) : '';
        const selected = (hubIds || []).map(String);
        if (selected.length && hid && !selected.includes(hid)) next.user_id = '';
      }
      return next;
    });
  };
  const setDailyBranchUser = (userId) => {
    const match = (users || []).find((u) => String(u.user_id) === String(userId));
    const hid = match?.location_hub_id != null ? String(match.location_hub_id) : '';
    setFilters((prev) => ({
      ...prev,
      user_id: userId || '',
      location_hub_ids: hid ? [hid] : (prev.location_hub_ids || []),
    }));
  };

  const requestSeq = React.useRef(0);
  filtersRef.current = filters;

  const applyPageResult = useCallback((page, data, seq) => {
    if (seq !== requestSeq.current) return;
    const pageInfo = data?.pagination || {};
    const more = Boolean(pageInfo.has_more);
    hasMoreRef.current = more;
    setHasMore(more);
    setPayload((prev) => mergeReportPage(prev, data, page));
  }, []);

  const fetchReportPage = useCallback(async (page, activeFilters, seq, { refresh = false } = {}) => {
    const params = {
      ...buildCleanParams(activeFilters),
      page,
      per_page: REPORT_PAGE_SIZE,
    };
    if (refresh) params.refresh = 1;
    const res = await reportService.getReportData(def.type, params);
    if (seq !== requestSeq.current) return null;
    if (res?.success === false) {
      throw new Error(res.error || 'Failed to load report');
    }
    return res?.data || res;
  }, [def]);

  const loadData = useCallback(async (filterOverride) => {
    if (!def) return null;
    const activeFilters = filterOverride || filtersRef.current;
    const params = buildCleanParams(activeFilters);
    const seq = ++requestSeq.current;
    hasMoreRef.current = false;
    setHasMore(false);
    setSummaryLoading(true);
    setRowsLoading(true);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    setError('');
    setPayload(null);
    try {
      const summaryRes = await reportService.getReportData(def.type, { ...params, summary_only: 1, refresh: 1 });
      if (seq !== requestSeq.current) return null;
      if (summaryRes?.success === false) {
        setError(summaryRes.error || 'Failed to load report');
        setPayload(null);
        return null;
      }
      const summary = summaryRes?.data || summaryRes;
      setPayload(summary);
      setSummaryLoading(false);
      const total = Number(summary?.pagination?.total ?? summary?.meta?.count ?? 0);
      if (total <= 0) {
        hasMoreRef.current = false;
        setHasMore(false);
        return params;
      }
      const pageData = await fetchReportPage(1, activeFilters, seq);
      if (!pageData || seq !== requestSeq.current) return params;
      applyPageResult(1, pageData, seq);
      return params;
    } catch (err) {
      if (seq !== requestSeq.current) return null;
      console.error(err);
      setError(err?.response?.data?.error || err.message || 'Failed to load report');
      setPayload(null);
      return null;
    } finally {
      if (seq === requestSeq.current) {
        setSummaryLoading(false);
        setRowsLoading(false);
      }
    }
  }, [def, fetchReportPage, applyPageResult]);

  const loadMore = useCallback(async () => {
    if (!def || loadingMoreRef.current || !hasMoreRef.current) return;
    const seq = requestSeq.current;
    const currentPage = Number(payload?.pagination?.page || 1);
    if (payload?.pagination?.summary_only) return;
    const nextPage = currentPage + 1;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const pageData = await fetchReportPage(nextPage, filtersRef.current, seq);
      if (pageData) applyPageResult(nextPage, pageData, seq);
    } catch (err) {
      if (seq === requestSeq.current) {
        setError(err?.response?.data?.error || err.message || 'Failed to load more records');
      }
    } finally {
      if (seq === requestSeq.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [def, payload, fetchReportPage, applyPageResult]);

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  // Reset filters when navigating between report types
  useEffect(() => {
    if (!def || !isReportVisible(def, user)) return;
    const defaults = applyScopedFilters(getDefaultFilters(def));
    setFilters(defaults);
    setPayload(null);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (!lockedHubIds) return;
    setFilters((prev) => {
      const cur = (prev.location_hub_ids || []).map(String).sort().join(',');
      const next = [...lockedHubIds].sort().join(',');
      if (cur === next) return prev;
      return { ...prev, location_hub_ids: lockedHubIds };
    });
  }, [lockedHubIds, type]);

  // Dynamically reload table whenever filters change (debounced)
  useEffect(() => {
    if (!def || !isReportVisible(def, user)) return undefined;
    const timer = setTimeout(() => {
      loadData(filters);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, type, def]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadMoreRef.current();
      }
    }, { root: null, rootMargin: '280px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, payload, type]);

  const gstExcelKey = React.useRef('');
  useEffect(() => {
    const gst = payload?.meta?.gst;
    if (type !== 'gst-collection' || !gst || gst.list_on_screen !== false) return undefined;
    const key = `${gst.from_date}|${gst.to_date}|${gst.collection_type}|${gst.gst_filter}`;
    if (gstExcelKey.current === key) return undefined;
    gstExcelKey.current = key;
    const params = {
      from_date: payload?.meta?.filters?.from_date || filters.from_date,
      to_date: payload?.meta?.filters?.to_date || filters.to_date,
      collection_type: gst.collection_type,
      gst_filter: gst.gst_filter,
    };
    let cancelled = false;
    (async () => {
      setExcelLoading(true);
      try {
        await reportService.downloadExcel('gst-collection', buildCleanParams(params));
      } catch (err) {
        if (!cancelled) alert(err?.response?.data?.error || 'Excel download failed');
      } finally {
        if (!cancelled) setExcelLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [type, payload, filters]);

  if (!def) {
    return (
      <DashboardLayout>
        <p>Unknown report type.</p>
        <button type="button" onClick={() => navigate('/reports')}>Back</button>
      </DashboardLayout>
    );
  }

  if (!isReportVisible(def, user)) {
    return (
      <DashboardLayout>
        <p>You do not have access to this report.</p>
        <button type="button" onClick={() => navigate('/reports')}>Back</button>
      </DashboardLayout>
    );
  }

  const c = colorMap[def.color] || colorMap.indigo;
  const Icon = def.icon;
  const columns = payload?.columns || [];
  const rows = payload?.rows || [];
  const totals = payload?.totals;
  const summary = payload?.meta?.summary;
  const extraTables = payload?.meta?.extra_tables || [];
  const branchLayout = payload?.meta?.branch_layout || summary?.branch_layout;
  const sections = payload?.sections || [];
  const isStatement = (payload?.layout === 'statement') || def.layout === 'statement' || sections.length > 0;
  const allowExcel = canExportExcel(user);

  const handleExcel = async () => {
    setExcelLoading(true);
    try {
      // Ensure export matches current filters (refresh table first if needed)
      const params = buildCleanParams(filters);
      await reportService.downloadExcel(def.type, params);
    } catch (err) {
      alert(err?.response?.data?.error || 'Excel download failed');
    } finally {
      setExcelLoading(false);
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const params = buildCleanParams(filters);
      await reportService.downloadPdf(def.type, params);
    } catch (err) {
      alert(err?.response?.data?.error || 'PDF download failed');
    } finally {
      setPdfLoading(false);
    }
  };

  const filterFields = (() => {
    switch (type) {
      case 'user':
        return (
          <>
            <ReportSelect label="User" value={filters.user_id} onChange={(v) => setF('user_id', v)} placeholder="All Users" options={userOptions} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'location':
        return (
          <>
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
            <CheckboxDropdown
              label="Branch"
              emptyLabel={lockedHubIds ? 'Your branch' : 'All Branches'}
              options={lockedHubIds
                ? hubIdOptions.filter((o) => lockedHubIds.includes(String(o.value)))
                : hubIdOptions}
              selected={filters.location_hub_ids || []}
              onChange={(v) => setF('location_hub_ids', v)}
              disabled={Boolean(lockedHubIds)}
            />
            <CheckboxDropdown
              label="From Station"
              emptyLabel="All Stations"
              options={hubIdOptions}
              selected={filters.from_station_ids || []}
              onChange={(v) => setF('from_station_ids', v)}
            />
            <CheckboxDropdown
              label="To Station"
              emptyLabel="All Stations"
              options={hubIdOptions}
              selected={filters.to_station_ids || []}
              onChange={(v) => setF('to_station_ids', v)}
            />
          </>
        );
      case 'vehicle':
        return (
          <>
            <ReportSelect label="Vehicle" value={filters.vehicle_id} onChange={(v) => setF('vehicle_id', v)} placeholder="All Vehicles" options={vehicleOptions} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'ml-sheet':
        return (
          <>
            <TextInput label="Dispatch ID" value={filters.ml_sheet_id} onChange={(v) => setF('ml_sheet_id', v)} placeholder="Leave blank for all" />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
            <CheckboxDropdown label="Sender Locations" options={locationOptions} selected={filters.sender_locations || []} onChange={(v) => setF('sender_locations', v)} />
            <CheckboxDropdown label="Receiver Locations" options={locationOptions} selected={filters.receiver_locations || []} onChange={(v) => setF('receiver_locations', v)} />
          </>
        );
      case 'shipment':
      case 'business':
        return (
          <>
            <TextInput label="Shipment ID" value={filters.shipment_id} onChange={(v) => setF('shipment_id', v)} placeholder="Leave blank for all" />
            <CheckboxDropdown
              label="Status"
              emptyLabel="All Statuses"
              options={[
                { value: 'Booked', label: 'Booked' },
                { value: 'In Transit', label: 'In Transit' },
                { value: 'Dispatched', label: 'Dispatched' },
                { value: 'Received', label: 'Received' },
                { value: 'Delivered', label: 'Delivered' },
                { value: 'Returned', label: 'Returned' },
                { value: 'Rejected', label: 'Rejected' },
              ]}
              selected={Array.isArray(filters.status) ? filters.status : (filters.status ? [filters.status] : [])}
              onChange={(v) => setF('status', v)}
            />
            <TextInput label="GST Number" value={filters.gst_number || ''} onChange={(v) => setF('gst_number', v)} placeholder="Sender or Receiver GST" />
            <TextInput label="Sender Contact" value={filters.sender_contact || ''} onChange={(v) => setF('sender_contact', v)} placeholder="Sender phone number" />
            <TextInput label="Receiver Contact" value={filters.receiver_contact || ''} onChange={(v) => setF('receiver_contact', v)} placeholder="Receiver phone number" />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
            <CheckboxDropdown label="Sender Locations" options={locationOptions} selected={filters.sender_locations || []} onChange={(v) => setF('sender_locations', v)} />
            <CheckboxDropdown label="Receiver Locations" options={locationOptions} selected={filters.receiver_locations || []} onChange={(v) => setF('receiver_locations', v)} />
            {type === 'business' && (
              <>
                <CheckboxDropdown
                  label="From Station"
                  emptyLabel="All Stations"
                  options={hubIdOptions}
                  selected={(filters.from_station_ids || []).map(String)}
                  onChange={(v) => setF('from_station_ids', v)}
                />
                <CheckboxDropdown
                  label="To Station"
                  emptyLabel="All Stations"
                  options={hubIdOptions}
                  selected={(filters.to_station_ids || []).map(String)}
                  onChange={(v) => setF('to_station_ids', v)}
                />
              </>
            )}
            <CheckboxDropdown
              label="Location Hub"
              emptyLabel="All Hubs"
              options={hubIdOptions}
              selected={filters.location_hub_ids || []}
              onChange={(v) => setF('location_hub_ids', v)}
            />
            <CheckboxDropdown
              label="Payment Modes"
              options={[
                { value: 'paid', label: 'Paid' }, { value: 'paid_balance', label: 'Paid Balance' },
                { value: 'cash', label: 'Cash' }, { value: 'to_pay', label: 'To Pay' },
                { value: 'credit', label: 'Credit / On Account' }, { value: 'upi', label: 'UPI' },
                { value: 'bank_transfer', label: 'Bank Transfer' }, { value: 'cheque', label: 'Cheque' },
                { value: 'online', label: 'Online' },
              ]}
              selected={filters.payment_modes || []}
              onChange={(v) => setF('payment_modes', v)}
            />
            <CheckboxDropdown
              label="Shipment Client"
              emptyLabel="All Clients"
              options={clientOptions}
              selected={(filters.shipment_client_ids || []).map(String)}
              onChange={(v) => setF('shipment_client_ids', v)}
            />
            {!isAgentUser && (
              <ReportSelect label="Agent / User" value={filters.user_id} onChange={(v) => setF('user_id', v)} placeholder="All Agents" options={userOptions} />
            )}
            <ReportSelect label="Vehicle" value={filters.vehicle_id} onChange={(v) => setF('vehicle_id', v)} placeholder="All Vehicles" options={vehicleOptions} />
          </>
        );
      case 'daily-business':
      case 'route-pl':
      case 'receivable':
      case 'payable':
        return (
          <>
            <FilterField>
              <label className={filterLabelClass}>Report Date</label>
              <input
                type="date"
                value={filters.report_date || ''}
                onChange={(e) => setF('report_date', e.target.value)}
                className={filterDateInputClass}
              />
            </FilterField>
            {type !== 'payable' && (
              <CheckboxDropdown
                label="Location Hub"
                emptyLabel="All Hubs"
                options={hubIdOptions}
                selected={filters.location_hub_ids || []}
                onChange={(v) => setF('location_hub_ids', v)}
              />
            )}
          </>
        );
      case 'daily-branch':
        return (
          <>
            <DateRangeRow
              label="Date range"
              fromValue={filters.from_date || ''}
              toValue={filters.to_date || ''}
              onFromChange={(v) => setF('from_date', v)}
              onToChange={(v) => setF('to_date', v)}
            />
            <CheckboxDropdown
              label="Location Hub"
              emptyLabel={lockedHubIds ? 'Assigned hub' : 'All Hubs'}
              options={lockedHubIds
                ? hubIdOptions.filter((o) => lockedHubIds.includes(String(o.value)))
                : hubIdOptions}
              selected={filters.location_hub_ids || []}
              onChange={(v) => setDailyBranchHubs(v)}
              disabled={Boolean(lockedHubIds)}
            />
            {!isAgentUser && (
              <ReportSelect
                label="User"
                value={filters.user_id || ''}
                onChange={(v) => setDailyBranchUser(v)}
                placeholder="All Users"
                options={dailyBranchUserOptions}
              />
            )}
          </>
        );
      case 'receive':
      case 'delivery-event':
      case 'ewaybills':
      case 'onaccount-credit':
      case 'invoice-cancellation':
      case 'party-booking':
        return (
          <>
            {['receive', 'delivery-event'].includes(type) && (
              <TextInput label="Shipment ID" value={filters.shipment_id || ''} onChange={(v) => setF('shipment_id', v)} placeholder="Leave blank for all" />
            )}
            <CheckboxDropdown
              label="Location Hub"
              emptyLabel="All Hubs"
              options={hubIdOptions}
              selected={filters.location_hub_ids || []}
              onChange={(v) => setF('location_hub_ids', v)}
            />
            <CheckboxDropdown
              label="Shipment Client"
              emptyLabel="All Clients"
              options={clientOptions}
              selected={(filters.shipment_client_ids || []).map(String)}
              onChange={(v) => setF('shipment_client_ids', v)}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date || ''} toValue={filters.to_date || ''} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'approved-transactions':
        return (
          <>
            <CheckboxDropdown
              label="Branch / Location"
              emptyLabel="All Branches"
              options={hubIdOptions}
              selected={filters.location_hub_ids || []}
              onChange={(v) => setF('location_hub_ids', v)}
            />
            <ReportSelect label="Category" value={filters.category || ''} onChange={(v) => setF('category', v)} placeholder="All Categories" options={expenseCategories} />
            <DateRangeRow label="Date range" fromValue={filters.from_date || ''} toValue={filters.to_date || ''} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'goods-balance':
      case 'branch-payable':
        return (
          <CheckboxDropdown
            label="Location Hub"
            emptyLabel="All Hubs"
            options={hubIdOptions}
            selected={filters.location_hub_ids || []}
            onChange={(v) => setF('location_hub_ids', v)}
          />
        );
      case 'expense':
        return (
          <>
            <CheckboxDropdown
              label="Branch / Location"
              emptyLabel="All Branches"
              options={hubIdOptions}
              selected={filters.location_hub_ids || []}
              onChange={(v) => setF('location_hub_ids', v)}
            />
            <ReportSelect label="Category" value={filters.category || ''} onChange={(v) => setF('category', v)} placeholder="All Categories" options={expenseCategories} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'on-contract':
      case 'vehicle-contract':
      case 'trip-contract':
        return (
          <>
            <TextInput label="Contract / Trip ID" value={filters.contract_id} onChange={(v) => setF('contract_id', v)} placeholder="Leave blank for all" />
            <ReportSelect label="Vehicle" value={filters.vehicle_id} onChange={(v) => setF('vehicle_id', v)} placeholder="All Vehicles" options={vehicleOptions} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'agent-tds':
        return (
          <>
            {!isAgentUser && (
              <ReportSelect label="Agent" value={filters.user_id || filters.agent_user_id} onChange={(v) => { setF('user_id', v); setF('agent_user_id', v); }} placeholder="All Agents" options={agentOptions} />
            )}
            <CheckboxDropdown
              label="Hub / Branch"
              emptyLabel="All Hubs"
              options={hubIdOptions}
              selected={filters.location_hub_ids || []}
              onChange={(v) => setF('location_hub_ids', v)}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'commission':
        return (
          <>
            <ReportSelect
              label="Contracted Party"
              value={filters.contract_party_id}
              onChange={(v) => setF('contract_party_id', v)}
              placeholder="All Contracted Parties"
              options={contractPartyOptions}
            />
            {!isAgentUser && (
              <ReportSelect label="Agent" value={filters.agent_user_id} onChange={(v) => setF('agent_user_id', v)} placeholder="All Agents" options={agentOptions} />
            )}
            <CheckboxDropdown
              label="Hub / Branch"
              emptyLabel="All Hubs"
              options={hubIdOptions}
              selected={filters.location_hub_ids || []}
              onChange={(v) => setF('location_hub_ids', v)}
            />
            <ReportSelect
              label="Status"
              value={filters.status}
              onChange={(v) => setF('status', v)}
              placeholder="All Status"
              options={[{ value: 'UNPAID', label: 'Unpaid' }, { value: 'PAID', label: 'Paid' }]}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'external-commission':
        return (
          <>
            <ReportSelect
              label="Vehicle Owner"
              value={filters.vehicle_owner_id}
              onChange={(v) => setF('vehicle_owner_id', v)}
              placeholder="All Vehicle Owners"
              options={(vehicleOwners || []).map((o) => ({
                value: o.vehicle_owner_id,
                label: o.owner_name || o.vehicle_owner_id,
              }))}
            />
            <CheckboxDropdown
              label="Booking Agents"
              options={(externalAgents.length
                ? externalAgents
                : (users || []).filter((u) => {
                  const role = typeof u.role === 'string' ? u.role : (u.role_name || u.role?.role_name || '');
                  return role === 'Agent';
                }).map((u) => ({ agent_id: u.user_id, agent_name: u.name || u.username }))
              ).map((a) => ({
                value: a.agent_id,
                label: a.agent_name || a.agent_id,
              }))}
              selected={filters.agent_user_ids || []}
              onChange={(v) => setF('agent_user_ids', v)}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'hr':
        return (
          <>
            <ReportSelect
              label="Report Type"
              value={filters.type}
              onChange={(v) => setF('type', v)}
              placeholder="Report Type"
              options={[
                { value: 'both', label: 'Attendance + Salary' },
                { value: 'attendance', label: 'Attendance Only' },
                { value: 'salary', label: 'Salary Only' },
              ]}
            />
            {!isAgentUser && (
              <ReportSelect label="User" value={filters.user_id} onChange={(v) => setF('user_id', v)} placeholder="All Users" options={userOptions} />
            )}
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
            <ReportSelect
              label="Month (Salary)"
              value={filters.month}
              onChange={(v) => setF('month', v)}
              placeholder="All Months"
              options={['january','february','march','april','may','june','july','august','september','october','november','december'].map((m) => ({
                value: m, label: m.charAt(0).toUpperCase() + m.slice(1),
              }))}
            />
            <ReportSelect
              label="Salary Status"
              value={filters.status}
              onChange={(v) => setF('status', v)}
              placeholder="All Status"
              options={[{ value: 'pending', label: 'Pending' }, { value: 'credited', label: 'Credited' }]}
            />
          </>
        );
      case 'gst-collection':
        return (
          <>
            <DateRangeRow
              label="Date range"
              fromValue={filters.from_date || ''}
              toValue={filters.to_date || ''}
              onFromChange={(v) => setF('from_date', v)}
              onToChange={(v) => setF('to_date', v)}
            />
            <FilterField>
              <label className={filterLabelClass}>Type</label>
              <select
                value={filters.collection_type || 'booking'}
                onChange={(e) => setF('collection_type', e.target.value)}
                className={filterSelectClass}
              >
                <option value="booking">Booking</option>
                <option value="transaction">Transaction</option>
              </select>
            </FilterField>
            <FilterField>
              <label className={filterLabelClass}>GST</label>
              <select
                value={filters.gst_filter || 'gst'}
                onChange={(e) => setF('gst_filter', e.target.value)}
                className={filterSelectClass}
              >
                <option value="gst">GST</option>
                <option value="non_gst">NON GST</option>
              </select>
            </FilterField>
          </>
        );
      default:
        return (
          <DateRangeRow label="Date range" fromValue={filters.from_date || ''} toValue={filters.to_date || ''} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
        );
    }
  })();

  return (
    <DashboardLayout>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div className="w-full max-w-full min-w-0">
        {/* Header: title + Excel / PDF / Back */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-3 sm:mb-4">
          <div className="flex items-start gap-2.5 sm:gap-3 min-w-0 flex-1">
            <div
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: c.iconBg, border: `1px solid ${c.buttonBorder}` }}
            >
              <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: c.iconColor }} />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-extrabold text-slate-900 m-0 leading-tight truncate">{def.title}</h1>
              <p className="text-[11px] text-slate-400 mt-0.5 m-0 leading-snug line-clamp-1 sm:line-clamp-2">{def.description}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0 lg:justify-end">
            {allowExcel && (
              <button
                type="button"
                onClick={handleExcel}
                disabled={excelLoading}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold disabled:opacity-60"
                style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857' }}
              >
                {excelLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
                Excel
              </button>
            )}
            <button
              type="button"
              onClick={handlePdf}
              disabled={pdfLoading}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold disabled:opacity-60"
              style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}
            >
              {pdfLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              PDF
            </button>
            <button
              type="button"
              onClick={() => navigate('/reports')}
              className="inline-flex items-center gap-1.5 shrink-0 border border-slate-200 bg-white rounded-lg px-2.5 py-2 text-xs font-semibold text-slate-600"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          </div>
        </div>

        {/* Filters + Clear / Refresh */}
        <div
          className="rounded-xl border border-indigo-100 p-3 sm:p-4 mb-3 sm:mb-4"
          style={{ background: 'linear-gradient(135deg, #ffffff 0%, #eef2ff 100%)' }}
        >
          <div className="flex items-center gap-2 mb-2.5 sm:mb-3 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
            <span className="text-sm font-semibold text-slate-800">Filters</span>
            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium hidden sm:inline">
              Current month default
            </span>
            <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={() => {
                  const defaults = applyScopedFilters(getDefaultFilters(def));
                  setFilters(defaults);
                  loadData(defaults);
                }}
                title="Clear filters"
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold border border-red-200 bg-white text-red-500 hover:bg-red-50"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                Clear
              </button>
              <button
                type="button"
                onClick={() => loadData()}
                disabled={summaryLoading || rowsLoading}
                title="Apply / Refresh"
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold disabled:opacity-60"
                style={{ background: c.buttonBg, border: `1px solid ${c.buttonBorder}`, color: c.buttonText }}
              >
                {(summaryLoading || rowsLoading)
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.25} />}
                {type === 'gst-collection' ? 'Generate' : 'Refresh'}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 [&>*]:min-w-0">
            {filterFields}
          </div>
          {type === 'gst-collection' && (
            <p className="mt-3 mb-0 text-[11px] text-amber-800">
              A date range above 7 days downloads Excel instead of listing the consignments. State-wise totals and the summary still show on this page.
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-[10px] p-3 mb-4 text-xs sm:text-[13px]">
            {error}
          </div>
        )}

        {type === 'daily-branch' && summary && !summaryLoading && (
          <div className={`grid grid-cols-2 sm:grid-cols-3 ${branchLayout === 'own' ? 'lg:grid-cols-5' : 'lg:grid-cols-6'} gap-2 sm:gap-3 mb-3 sm:mb-4`}>
            {[
              { label: 'Booked', count: summary.booked_count, amount: summary.booked_amount },
              { label: 'Dispatched', count: summary.dispatched_count, amount: summary.dispatched_amount },
              { label: 'ITCL Created', count: summary.itcl_count, amount: null },
              { label: 'Paid', count: summary.paid_count, amount: summary.paid_amount },
              ...(branchLayout === 'own'
                ? []
                : [
                    { label: 'To Pay', count: summary.topay_count, amount: summary.topay_amount },
                    { label: 'TDS', count: null, amount: summary.tds_amount, moneyLabel: 'TDS' },
                  ]),
              {
                label: summary.outstanding_direction === 'Payable' ? 'Payable' : summary.outstanding_direction === 'Settled' ? 'Settled' : 'Receivable',
                count: null,
                amount: summary.outstanding_amount,
                tone: summary.outstanding_direction === 'Payable' ? 'payable' : summary.outstanding_direction === 'Settled' ? 'settled' : 'receivable',
              },
            ].map((card) => (
              <div
                key={card.label}
                className={`rounded-xl border p-3 shadow-sm ${
                  card.tone === 'payable'
                    ? 'border-red-200 bg-red-50'
                    : card.tone === 'receivable'
                      ? 'border-emerald-200 bg-emerald-50'
                      : card.tone === 'settled'
                        ? 'border-slate-200 bg-slate-50'
                        : 'border-slate-200 bg-white'
                }`}
              >
                <p className="text-[11px] font-medium text-slate-500">{card.label}</p>
                {card.count != null && (
                  <p className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{card.count || 0}</p>
                )}
                {card.amount != null && (
                  <p className={`${card.count != null ? 'text-xs font-semibold text-slate-600' : 'text-lg font-bold text-slate-900'} tabular-nums mt-0.5`}>
                    {card.moneyLabel
                      ? formatReportCell(card.moneyLabel, card.amount, formatCurrencyWhole(card.amount))
                      : formatCurrencyWhole(card.amount)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {type === 'gst-collection' && (
          <GstCollectionReportView payload={payload} loading={summaryLoading} recordsLoading={rowsLoading} />
        )}

        {type === 'location' && !summaryLoading && (
          <LocationTargetBoard
            rows={payload?.meta?.location_targets?.rows || []}
            periodMonths={payload?.meta?.location_targets?.period_months || payload?.meta?.period_months || 1}
            usedCurrentMonth={Boolean(payload?.meta?.location_targets?.used_current_month)}
            asOf={payload?.meta?.location_targets?.to_date}
          />
        )}

        {type !== 'gst-collection' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
            <span className="text-xs sm:text-[13px] font-bold text-slate-900 truncate">{payload?.title || def.title}</span>
            <span className="text-[11px] sm:text-xs text-slate-500 break-words">
              {summaryLoading
                ? 'Loading totals…'
                : rowsLoading && rows.length === 0
                  ? 'Loading records…'
                  : isStatement
                    ? `${sections.reduce((n, s) => n + (s.rows || []).length, 0)} of ${payload?.pagination?.total ?? sections.reduce((n, s) => n + (s.rows || []).length, 0)} line(s)`
                    : `${rows.length} of ${payload?.pagination?.total ?? rows.length} record(s)`}
              {totals && Object.keys(totals).length > 0 && type !== 'daily-branch' && (
                <span className="block sm:inline sm:before:content-['·_'] mt-0.5 sm:mt-0">
                  {Object.entries(totals).map(([k, v]) => `${k}: ${formatReportCell(k, v, String(v ?? ''))}`).join(' · ')}
                </span>
              )}
            </span>
          </div>

          {payload?.meta?.warning && (
            <div className="mx-3 mt-3 mb-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {payload.meta.warning}
            </div>
          )}

          {isStatement ? (
            <div className="p-2.5 sm:p-4 space-y-4">
              {(summaryLoading || (rowsLoading && sections.every((section) => !(section.rows || []).length))) && (
                <div className="text-center text-slate-400 text-sm py-8">
                  {summaryLoading ? 'Loading totals…' : 'Loading records…'}
                </div>
              )}
              {!summaryLoading && !rowsLoading && sections.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-8">No data for the selected filters.</div>
              )}
              {!summaryLoading && sections.some((section) => (section.rows || []).length > 0) && sections.map((section, sidx) => {
                const cols = section.columns || columns;
                const srows = section.rows || [];
                const rowMeta = section.row_meta || [];
                return (
                  <div key={sidx} className="rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-3 py-2 text-xs font-bold text-slate-800" style={{ background: '#B4C7E7' }}>
                      {section.title}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="border-collapse text-xs" style={{ minWidth: `${Math.max(520, (cols.length || 3) * 88)}px` }}>
                        <thead>
                          <tr>
                            {cols.map((col, cidx) => (
                              <th
                                key={`${col}-${cidx}`}
                                className={`text-left px-3 py-2 whitespace-nowrap font-bold border-b border-sky-300 ${
                                  cidx === 0 ? 'sticky left-0 z-10' : cidx === 1 ? 'sticky left-[9.5rem] z-10' : ''
                                }`}
                                style={{ background: '#D6E3F8', color: '#1F4E79', minWidth: cidx < 2 ? 140 : 76 }}
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {srows.map((row, idx) => {
                            const meta = rowMeta[idx] || {};
                            const highlight = meta.is_nrr || section.highlight === 'total_nrr';
                            return (
                              <tr
                                key={idx}
                                style={{
                                  background: highlight
                                    ? (section.highlight === 'total_nrr' ? '#C6EFCE' : '#D9E1F2')
                                    : (idx % 2 ? '#F8FAFC' : '#fff'),
                                  fontWeight: highlight ? 700 : 400,
                                }}
                              >
                                {(Array.isArray(row) ? row : cols.map((col) => row[col])).map((cell, cidx) => (
                                  <td
                                    key={cidx}
                                    className={`px-3 py-1.5 border-b border-slate-200 whitespace-nowrap ${
                                      cidx === 0 ? 'sticky left-0 z-[1]' : cidx === 1 ? 'sticky left-[9.5rem] z-[1]' : ''
                                    }`}
                                    style={{
                                      background: highlight
                                        ? (section.highlight === 'total_nrr' ? '#C6EFCE' : '#D9E1F2')
                                        : (idx % 2 ? '#F8FAFC' : '#fff'),
                                    }}
                                  >
                                    {cell == null || cell === '' ? '—' : formatReportCell(cols[cidx], cell, '—')}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                          {section.totals_row && (
                            <tr style={{ background: '#B4C7E7', fontWeight: 700 }}>
                              {(section.totals_row || []).map((cell, cidx) => (
                                <td key={cidx} className="px-3 py-2 border-b border-sky-300 whitespace-nowrap">
                                  {cell == null ? '' : formatReportCell(cols[cidx], cell, '')}
                                </td>
                              ))}
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              {/* Mobile / iPad portrait: card list */}
              <div className="md:hidden p-2.5 sm:p-3 space-y-2">
                {(summaryLoading || (rowsLoading && rows.length === 0)) && (
                  <div className="text-center text-slate-400 text-sm py-8">
                    {summaryLoading ? 'Loading totals…' : 'Loading records…'}
                  </div>
                )}
                {!summaryLoading && !rowsLoading && rows.length === 0 && (
                  <div className="text-center text-slate-400 text-sm py-8">No data for the selected filters.</div>
                )}
                {rows.map((row, idx) => {
                  const cells = Array.isArray(row) ? row : columns.map((col) => row[col]);
                  const primary = String(cells[0] ?? `#${idx + 1}`);
                  const isTotals = primary === 'TOTALS' || primary === 'Total';
                  const isCumulative = primary === 'CUMULATIVE TOTALS';
                  const isOutstanding = primary === 'OUTSTANDING BALANCES';
                  const isFooter = isTotals || isCumulative || isOutstanding;
                  return (
                    <div
                      key={idx}
                      className="rounded-xl border p-3"
                      style={{
                        borderColor: isOutstanding ? '#F6D060' : isFooter ? '#93C5FD' : '#E2E8F0',
                        background: isOutstanding ? '#FFF2CC' : isFooter ? '#B4C7E7' : 'rgba(248,250,252,0.8)',
                      }}
                    >
                      <div className="text-xs font-bold text-slate-800 mb-2 truncate">{primary}</div>
                      <div className="grid grid-cols-1 gap-1.5">
                        {columns.map((col, cidx) => (
                          <div key={col} className="flex justify-between gap-3 text-[11px] leading-snug">
                            <span className="text-slate-500 font-semibold shrink-0 max-w-[45%]">{col}</span>
                            <span className="text-slate-800 text-right break-all">
                              {cells[cidx] == null || cells[cidx] === ''
                                ? '—'
                                : String(col).toLowerCase().startsWith('progress')
                                  ? (
                                    <span className="inline-block min-w-[6.5rem] text-left">
                                      <TargetProgressBar percent={cells[cidx]} />
                                      <span className="text-[11px] font-semibold">{cells[cidx]}%</span>
                                    </span>
                                  )
                                  : formatReportCell(col, cells[cidx], '—')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop / iPad landscape: table — horizontal scroll only; page scrolls vertically */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse text-xs min-w-[640px]">
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col}
                          className="text-left px-3 py-2.5 whitespace-nowrap font-bold border-b border-sky-300"
                          style={{ background: '#B4C7E7', color: '#1F4E79' }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(summaryLoading || (rowsLoading && rows.length === 0)) && (
                      <tr>
                        <td colSpan={Math.max(columns.length, 1)} className="px-6 py-8 text-center text-slate-400">
                          {summaryLoading ? 'Loading totals…' : 'Loading records…'}
                        </td>
                      </tr>
                    )}
                    {!summaryLoading && !rowsLoading && rows.length === 0 && (
                      <tr>
                        <td colSpan={Math.max(columns.length, 1)} className="px-6 py-8 text-center text-slate-400">
                          No data for the selected filters.
                        </td>
                      </tr>
                    )}
                    {rows.map((row, idx) => {
                      const cells = Array.isArray(row) ? row : columns.map((col) => row[col]);
                      const footerLabel = String(cells[0] || '');
                      const isTotals = footerLabel === 'TOTALS' || footerLabel === 'Total';
                      const isCumulative = footerLabel === 'CUMULATIVE TOTALS';
                      const isOutstanding = footerLabel === 'OUTSTANDING BALANCES';
                      const isFooter = isTotals || isCumulative || isOutstanding;
                      return (
                      <tr
                        key={idx}
                        style={{
                          background: isOutstanding
                            ? '#FFF2CC'
                            : isFooter
                              ? '#B4C7E7'
                              : (idx % 2 ? '#F2F7FF' : '#fff'),
                          fontWeight: isFooter ? 700 : 400,
                        }}
                      >
                        {cells.map((cell, cidx) => (
                          <td key={cidx} className="px-3 py-2 border-b border-slate-200 whitespace-nowrap">
                            {cell == null || cell === '' ? '' : (
                              String(columns[cidx] || '').toLowerCase().startsWith('progress')
                                ? (
                                  <span className="inline-block min-w-[7rem]">
                                    <TargetProgressBar percent={cell} />
                                    <span className="text-[11px] font-semibold">{cell}%</span>
                                  </span>
                                )
                                : formatReportCell(columns[cidx], cell, '')
                            )}
                          </td>
                        ))}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {(hasMore || loadingMore) && type !== 'gst-collection' && (
            <div ref={sentinelRef} className="py-4 text-center text-xs font-semibold text-slate-500">
              {loadingMore ? 'Loading more records…' : 'Scroll for more records'}
            </div>
          )}
        </div>
        )}

        {type === 'gst-collection' && (hasMore || loadingMore) && (
          <div ref={sentinelRef} className="py-4 text-center text-xs font-semibold text-slate-500">
            {loadingMore ? 'Loading more records…' : 'Scroll for more records'}
          </div>
        )}

        {type === 'daily-branch' && extraTables.some((table) => (table.rows || []).length > 0) && extraTables.map((table) => (
          <div key={table.title} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm mt-3">
            <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-200">
              <span className="text-xs sm:text-[13px] font-bold text-slate-900">{table.title}</span>
              <span className="text-[11px] text-slate-500 ml-2">{(table.rows || []).length} record(s)</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs min-w-[480px]">
                <thead>
                  <tr>
                    {(table.columns || []).map((col) => (
                      <th
                        key={col}
                        className="text-left px-3 py-2.5 whitespace-nowrap font-bold border-b border-sky-300"
                        style={{ background: '#B4C7E7', color: '#1F4E79' }}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(table.rows || []).map((row, idx) => (
                    <tr key={idx} style={{ background: idx % 2 ? '#F2F7FF' : '#fff' }}>
                      {(Array.isArray(row) ? row : (table.columns || []).map((col) => row[col])).map((cell, cidx) => (
                        <td key={cidx} className="px-3 py-2 border-b border-slate-200 whitespace-nowrap">
                          {cell == null ? '' : formatReportCell((table.columns || [])[cidx], cell, '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
};

export default ReportDetailPage;
